import "./AssetField.scss";

import clsx from "clsx";
import { get } from "lodash-es";
import { FC, Suspense, lazy, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Doc } from "sharedb/lib/client";

import type {
  AssetFieldType,
  PathType,
  PdfState,
  VideoState,
  WorksheetType,
} from "../../../shared/types/worksheet";
import { deleteBlockFromWorksheet } from "../../../shared/worksheets/doc-helpers";

import { ImageAsset } from "./ImageAsset";
import { PdfPageToolbar } from "./PdfPageToolbar";
import VideoAsset from "./VideoAsset";
import { ZoomToolbar } from "./ZoomToolbar";

import { t } from "i18n/i18n";
import { CloseButton } from "mds/components/CloseButton";
import { LegacyButton } from "mds/components/LegacyButton";
import { Menu, MenuItem, MenuRadioGroup, SubMenu } from "mds/components/Menu";
import { showConfirmationModal } from "mds/components/Modal";
import { useHoverable } from "mds/hooks/use-hoverable";
import {
  ArrowDownIcon,
  DotMenuVerticalIcon,
  ExpandFullScreenIcon,
  EyeVisibleIcon,
  LoadingDotsIcon,
  TrashDeleteIcon,
  WhiteboardIcon,
} from "mds/icons";
import { PaperClipLinkIcon } from "mds/icons/PaperClipLink";
import { DocViewContext } from "providers/DocViewProvider";
import { ShareDBContext } from "providers/ShareDBProvider";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectFeaturingPdfState, selectIsEditModeEnabled } from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { toastLocalizedOperationCatcher } from "utils/alerts";
import {
  clearPdfStateOnDoc,
  clearVideoStateOnDoc,
  getDefaultPdfState,
  getDefaultVideoState,
  setPdfStateOnDoc,
  setVideoStateOnDoc,
} from "utils/asset";
import { useAv } from "utils/av";
import { usePresentation } from "utils/presentation";
import { isImageFile, isPDFFile, isVideoFile } from "worksheets/helpers/files";

const PdfAsset = lazy(() => import("./PdfAsset"));

type AssetModalProps = {
  shortName: string;
  name: string;
  size: string;
  blockId: string;
  url: string;
  canAuthorPage: boolean;
  isImage: boolean;
  isVideo: boolean;
  isPdf: boolean;
  annotations: boolean;
  fieldId: string;
  onClose: (manualClose?: boolean) => void;
  onDelete: () => void;
};

const AssetModal: FC<AssetModalProps> = ({
  shortName,
  name,
  size,
  url,
  blockId,
  canAuthorPage,
  isImage,
  isVideo,
  isPdf,
  annotations,
  fieldId,
  onClose,
  onDelete,
}) => {
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);

  const [pdfState, setPdfState] = useState<PdfState>(getDefaultPdfState());

  const onPdfStateChange = (newState: Partial<PdfState>) => {
    setPdfState((prevState) => ({ ...prevState, ...newState }));
  };

  return (
    <>
      <div className="modal-preview__toolbar flex w-full items-center justify-between">
        <div className="ml-1 flex items-center gap-2">
          <h4 className="title h4 m-0 flex items-center gap-2">
            <PaperClipLinkIcon />
            <span className="break-words pr-2">
              <span className="mr-2">{shortName}</span>
              <span className="body-s whitespace-nowrap text-black-tint-70">{`(${size})`}</span>
            </span>
          </h4>
        </div>

        <div className="flex items-center gap-2">
          {canAuthorPage && isEditModeEnabled && (
            <Menu
              menuButton={
                <LegacyButton kind="tertiary" size="s">
                  <DotMenuVerticalIcon />
                </LegacyButton>
              }
            >
              <MenuItem download={name} href={url} allowPropagation>
                <ArrowDownIcon />
                {t("common.download")}
              </MenuItem>

              <MenuItem kind="destructive" onClick={onDelete}>
                <TrashDeleteIcon /> {t("common.delete")}
              </MenuItem>
            </Menu>
          )}

          {isPdf && (
            <>
              <ZoomToolbar setZoom={(zoom) => onPdfStateChange({ zoom })} zoom={pdfState.zoom} />
              <PdfPageToolbar pdfState={pdfState} setPdfState={onPdfStateChange} />
            </>
          )}

          {(!canAuthorPage || !isEditModeEnabled) && (
            <LegacyButton download={name} kind="secondary" size="s" to={url} iconOnly>
              <ArrowDownIcon />
            </LegacyButton>
          )}

          <CloseButton onClick={() => onClose()} />
        </div>
      </div>

      {isImage && (
        <ImageAsset
          annotations={annotations}
          blockId={blockId}
          fieldId={fieldId}
          name={name}
          url={url}
          isInModal
        />
      )}

      {isVideo && <VideoAsset className="enlarged-preview__media" id={blockId} url={url} />}

      {isPdf && (
        <Suspense fallback={<LoadingDotsIcon />}>
          <PdfAsset
            annotations={annotations}
            blockId={blockId}
            className="enlarged-preview__media"
            fieldId={fieldId}
            pdfState={pdfState}
            setPdfState={onPdfStateChange}
            url={url}
            isInModal
          />
        </Suspense>
      )}
    </>
  );
};

interface AssetFieldProps extends AssetFieldType {
  path: PathType;
  doc: Doc<WorksheetType>;
  blockId: string;
  blockHeight: number;
}

export const AssetField: FC<AssetFieldProps> = ({ id, path, doc, blockId, blockHeight }) => {
  const {
    name,
    size,
    url,
    fileType,
    view,
    annotations,
    pdfState: presentingPdfState,
    videoState: presentingVideoState,
  } = get(doc?.data, path) as AssetFieldType;
  const { isHovering, hoverParentProps } = useHoverable();
  const { canAuthorPage, isProjecting, isFeaturing } = useContext(DocViewContext);
  const { presentedBlockId, isFollowing, isPresenting } = usePresentation();
  const featuringPdfState = useAppSelector(selectFeaturingPdfState);
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const docContext = useContext(DocViewContext);
  const shareDBContextValue = useContext(ShareDBContext);
  const dispatch = useAppDispatch();
  const presenterUpdatedAssetState = useRef(false);
  const { isInVirtualClass } = useAv();

  const isFeaturedAsset = isFeaturing && presentedBlockId === blockId;

  // These are for PDFs only
  const [assetPdfState, setAssetPdfState] = useState<PdfState>(
    isProjecting || isFollowing ? presentingPdfState || getDefaultPdfState() : getDefaultPdfState(),
  );

  const isInline = view === "inline";
  const isCondensed = view === "condensed";

  const isImage = !!url && isImageFile(fileType);
  const isVideo = !!url && isVideoFile(fileType);
  const isPdf = !!url && isPDFFile(fileType);
  const isFollowingAsset = isFollowing && (isPdf || isInVirtualClass);
  const shouldSaveAssetStateInDoc = isPresenting && (isPdf || isInVirtualClass);
  const previousPdfState = useRef<PdfState | null>(
    presentingPdfState ? { ...presentingPdfState } : null,
  );
  const hasThumbnail = (isImage || isVideo) && isCondensed;
  const canPreview = isImage || isVideo || isPdf;
  const shortName = name.length > 50 ? `${name.slice(0, 50)}...` : name;
  const userOverridingPdfAssetState = Boolean(
    isFeaturedAsset ? featuringPdfState?.userUpdated : assetPdfState?.userUpdated,
  );

  const showAnnotationsOptions = isPdf || isImage;

  const onAnnotationsChange = () => {
    doc.submitOp([{ p: path.concat("annotations"), oi: !annotations, od: annotations }]);
  };

  const onDelete = useCallback(() => {
    const blockIndex = Number(path[1]);
    deleteBlockFromWorksheet(doc, blockIndex);
  }, [doc, path]);

  const onViewChange = (newView: AssetFieldType["view"]) => {
    doc.submitOp([{ p: path.concat("view"), oi: newView, od: view }]);
  };

  // If the asset is pdf and we're presenting we need to default the assetState
  const getPdfState = () => {
    if (shouldSaveAssetStateInDoc) {
      return presentingPdfState || getDefaultPdfState();
    }

    if (isFeaturedAsset) {
      return featuringPdfState;
    }

    return assetPdfState;
  };

  // If we change the zoom or page of the pdf we either update the copy here or save it
  // in the doc if we're presenting so that others can sync with our changes
  const setPdfState = (pdfState: Partial<PdfState>) => {
    if (shouldSaveAssetStateInDoc) {
      setPdfStateOnDoc(doc, path, getPdfState(), pdfState);
      presenterUpdatedAssetState.current = true;
    } else if (isFeaturedAsset) {
      dispatch(viewStateActions.setFeaturingPdfState(pdfState));
    } else {
      setAssetPdfState({ ...assetPdfState, ...pdfState });
    }
  };

  // We get the video state from the doc if we're presenting or following, but we only
  // need to default it if we're presenting.
  const getVideoState = () => {
    if (shouldSaveAssetStateInDoc) {
      return presentingVideoState || getDefaultVideoState();
    }

    if (isFollowingAsset) {
      return presentingVideoState;
    }

    return undefined;
  };

  // As the video controls live with the video asset (unlike the pdf controls)
  // we don't need to save state outside of the video asset.  But if we're
  // presenting we want to save it to the doc for others to sync with.
  const setVideoState = (videoState: Partial<VideoState>) => {
    if (shouldSaveAssetStateInDoc) {
      setVideoStateOnDoc(doc, path, getVideoState(), videoState);
      presenterUpdatedAssetState.current = true;
    }
  };

  // Check for instructor change to pdfstate if we're following
  useEffect(() => {
    if (isFollowingAsset && presentingPdfState) {
      // If the user has diverged from the presenting state we only force them back if the page changes
      if (
        userOverridingPdfAssetState &&
        previousPdfState.current?.pageNumber === presentingPdfState.pageNumber
      ) {
        return;
      }

      previousPdfState.current = { ...presentingPdfState };
      if (isFeaturedAsset) {
        dispatch(
          viewStateActions.setFeaturingPdfState({ ...presentingPdfState, userUpdated: false }),
        );
      } else {
        setAssetPdfState({ ...presentingPdfState, userUpdated: false });
      }
    }
  }, [
    isFollowingAsset,
    // eslint-disable-next-line no-object-deps/no-object-deps
    presentingPdfState,
    isFeaturedAsset,
    dispatch,
    userOverridingPdfAssetState,
  ]);

  // Clear asset state when we stop presenting or unmount
  useEffect(() => {
    if (!isPresenting && presenterUpdatedAssetState.current) {
      if (isPdf) {
        clearPdfStateOnDoc(doc, path);
      } else if (isVideo) {
        clearVideoStateOnDoc(doc, path);
      }

      presenterUpdatedAssetState.current = false; // Only do this once.
    }

    return () => {
      if (presenterUpdatedAssetState.current) {
        if (isPdf) {
          clearPdfStateOnDoc(doc, path);
        } else {
          clearVideoStateOnDoc(doc, path);
        }

        presenterUpdatedAssetState.current = false; // Only do this once.
      }
    };
    // eslint-disable-next-line no-object-deps/no-object-deps
  }, [isPresenting, doc, path, isPdf, isVideo]);

  const onShowModal = useCallback(
    async () => {
      const onFeatureClose = () => dispatch(viewStateActions.setAssetModalIsOpen(false));

      await showConfirmationModal({
        hideCloseButton: true,
        className: clsx("modal-preview bg-white", {
          "flex flex-col items-center": isImage,
        }),
        hideCancelConfirmButtons: true,
        fullScreen: true,
        allowManualClose: true,
        onClose: onFeatureClose,
        // ShareDBContext is required for the WhiteboardField, which we need to pass down to the Modal
        shareDBContextValue,
        // This modal will not be getting updated so this nesting issue is not a problem
        // eslint-disable-next-line react/no-unstable-nested-components
        children: (onClose: () => void) => {
          if (!url) {
            return null;
          }

          return (
            // DocViewContext is required for the WhiteboardField, so we need to add it here
            // Note: this DocViewContext will not have the correct value for isFeaturing
            <DocViewContext.Provider value={docContext}>
              <AssetModal
                annotations={Boolean(annotations)}
                blockId={blockId}
                canAuthorPage={canAuthorPage}
                fieldId={id}
                isImage={isImage}
                isPdf={isPdf}
                isVideo={isVideo}
                name={name}
                shortName={shortName}
                size={size}
                url={url}
                onClose={onClose}
                onDelete={onDelete}
              />
            </DocViewContext.Provider>
          );
        },
      });

      return null;
    },
    // TODO: look into simplifying this list
    [
      canAuthorPage,
      isImage,
      isPdf,
      blockId,
      isVideo,
      dispatch,
      name,
      shortName,
      size,
      url,
      onDelete,
      annotations,
      docContext,
      id,
      shareDBContextValue,
    ],
  );

  const onView = () => {
    dispatch(viewStateActions.setAssetModalIsOpen(true));
    onShowModal().catch(toastLocalizedOperationCatcher("preview_failure"));
  };

  if (canPreview && isInline) {
    return (
      <div className="w-full">
        <div className="inline-preview" {...hoverParentProps} role="toolbar">
          {/* TODO: For projector view, we are temporarily not allow closing or any other manipulation of the asset */}
          {!isFeaturedAsset && (
            <div
              className={clsx(
                "inline-preview__toolbar flex items-center justify-between",
                isHovering && "--hover",
              )}
            >
              <div className="ml-1 flex items-center gap-2">
                <h4 className="title h4 m-0 flex items-center gap-2">
                  {!hasThumbnail && <PaperClipLinkIcon className="mt-1" />}

                  <span className="break-words pr-2">
                    <span className="mr-2">{shortName}</span>
                    <span className="body-s whitespace-nowrap text-black-tint-70">{`(${size})`}</span>
                  </span>
                </h4>
              </div>

              <div className="flex items-center gap-2">
                {!isFeaturedAsset && (
                  <>
                    {isPdf && (
                      <>
                        <ZoomToolbar
                          setZoom={(zoom) => setPdfState({ zoom, userUpdated: isFollowing })}
                          zoom={getPdfState().zoom}
                        />

                        <PdfPageToolbar
                          pdfState={getPdfState()}
                          setPdfState={(state) =>
                            setPdfState({ ...state, userUpdated: isFollowing })
                          }
                        />
                      </>
                    )}

                    {(!canAuthorPage || !isEditModeEnabled) && (
                      <LegacyButton download={name} kind="secondary" size="s" to={url} iconOnly>
                        <ArrowDownIcon />
                      </LegacyButton>
                    )}

                    <ViewButton onView={onView} />

                    {canAuthorPage && isEditModeEnabled && (
                      <Menu
                        menuButton={
                          <LegacyButton kind="tertiary" size="s">
                            <DotMenuVerticalIcon />
                          </LegacyButton>
                        }
                      >
                        <MenuItem download={name} href={url} allowPropagation>
                          <ArrowDownIcon /> {t("common.download")}
                        </MenuItem>
                        <ViewOptionsMenu value={view} onRadioChange={onViewChange} />
                        {showAnnotationsOptions && annotations ? (
                          <MenuItem kind="destructive" onClick={onAnnotationsChange}>
                            <WhiteboardIcon /> {t("common.disable_annotations")}
                          </MenuItem>
                        ) : showAnnotationsOptions && !annotations ? (
                          <MenuItem onClick={onAnnotationsChange}>
                            <WhiteboardIcon /> {t("common.enable_annotations")}
                          </MenuItem>
                        ) : null}
                        <MenuItem kind="destructive" onClick={onDelete}>
                          <TrashDeleteIcon /> {t("common.delete")}
                        </MenuItem>
                      </Menu>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {isImage && (
            <ImageAsset
              annotations={annotations}
              blockHeight={blockHeight}
              blockId={blockId}
              fieldId={id}
              isFeaturingAsset={isFeaturedAsset}
              name={name}
              url={url}
            />
          )}

          {isVideo && (
            <VideoAsset
              blockHeight={blockHeight}
              className="inline-preview__media"
              id={blockId}
              setVideoState={setVideoState}
              shouldSyncWithInstructorView={isProjecting}
              url={url}
              videoState={getVideoState()}
            />
          )}

          {isPdf && (
            <Suspense fallback={<LoadingDotsIcon />}>
              <PdfAsset
                annotations={annotations}
                blockId={blockId}
                className="inline-preview__media"
                fieldId={id}
                isFeaturingAsset={isFeaturedAsset}
                pdfState={getPdfState()}
                setPdfState={setPdfState}
                shouldSyncWithInstructorView={isProjecting}
                url={url}
              />
            </Suspense>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={clsx("flex-start flex w-full", hasThumbnail && "-with-thumbnail")}>
      {hasThumbnail && (
        <div className="preview-container">
          {isImage && <ImageAsset name={name} url={url} />}

          {isVideo && <video className="preview-container__media" src={url} controls />}
        </div>
      )}

      <div className="body-s container w-full gap-2">
        <div className="flex items-center justify-between">
          <h4 className="title h4 m-0 flex items-center gap-2">
            {!hasThumbnail && <PaperClipLinkIcon className="mt-1" />}

            <span className="break-words pr-2">
              <span className="mr-2">{shortName}</span>
              <span className="body-s whitespace-nowrap text-black-tint-70">{`(${size})`}</span>
            </span>
          </h4>

          <div className="flex items-center gap-2">
            {canPreview ? (
              <>
                <LegacyButton download={name} kind="secondary" size="s" to={url} iconOnly>
                  <ArrowDownIcon />
                </LegacyButton>

                <ViewButton onView={onView} />
              </>
            ) : url ? (
              <LegacyButton className="gap-2" download={name} kind="secondary" size="s" to={url}>
                <ArrowDownIcon />
                {t("common.download")}
              </LegacyButton>
            ) : null}

            {(canAuthorPage || canPreview) && (
              <Menu
                menuButton={
                  <LegacyButton kind="tertiary" size="m">
                    <DotMenuVerticalIcon />
                  </LegacyButton>
                }
              >
                {canPreview && (
                  <MenuItem className="gap-2" download={name} href={url} allowPropagation>
                    <ArrowDownIcon />
                    {t("common.download")}
                  </MenuItem>
                )}

                {canPreview && canAuthorPage && (
                  <ViewOptionsMenu value={view} onRadioChange={onViewChange} />
                )}

                {canAuthorPage && (
                  <MenuItem kind="destructive" onClick={onDelete}>
                    <TrashDeleteIcon /> {t("common.delete")}
                  </MenuItem>
                )}
              </Menu>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

type ViewButtonProps = {
  onView: () => void;
};

export const ViewButton: FC<ViewButtonProps> = ({ onView }) => {
  const { isPresenting } = usePresentation();

  if (isPresenting) return null;

  return (
    <LegacyButton className="gap-2" kind="secondary" size="s" onClick={onView}>
      <ExpandFullScreenIcon />
      {t("asset_field.view")}
    </LegacyButton>
  );
};

type ViewOptionsMenuProps = {
  value: AssetFieldType["view"];
  onRadioChange: (newView: AssetFieldType["view"]) => void;
};

export const ViewOptionsMenu: FC<ViewOptionsMenuProps> = ({ value, onRadioChange }) => {
  return (
    <SubMenu
      className="gap-2"
      label={
        <>
          {/* TODO: Replace with different "eye" icon once it is added to the icon list in Figma */}
          <EyeVisibleIcon />
          {/* TODO: Is there a better way to localize SubMenu? We probably want Display As Condensed Preview or
           *   Display As Inline Preview rather than three substrings: Display As, Inline Preview, and
           *   Condensed Preview */}
          {t("asset_field.display_as")}
        </>
      }
      withArrow
    >
      <MenuRadioGroup
        value={value}
        onRadioChange={(e) => onRadioChange(e.value as AssetFieldType["view"])}
      >
        <MenuItem kind="radio" type="radio" value="inline">
          {t("asset_field.inline_preview")}
        </MenuItem>
        <MenuItem kind="radio" type="radio" value="condensed">
          {t("asset_field.condensed_preview")}
        </MenuItem>
      </MenuRadioGroup>
    </SubMenu>
  );
};
